
import {Component, Vue} from "vue-property-decorator";
import {safetyModule} from "@/store";
import {formatDate, formatDateTime} from "@/auxillary/date-format";
import {translate} from "@/auxillary/translation";
import {MdbsCore} from "@/api/tsclient";
import {deepCopy} from "@/auxillary";
import ISafetyReportConfigurationEntry = MdbsCore.ISafetyReportConfigurationEntry;
import SafetyReportConfigurationUpdateDto = MdbsCore.SafetyReportConfigurationUpdateDto;
import SafetyReportConfigurationUpdateEntry = MdbsCore.SafetyReportConfigurationUpdateEntry;

enum FieldType {
  Instrument, Visit, Patient,
}

interface ExpansionHeader {
  parentEntityIdentifier: string;
  displayKey: string;
  selectedCount: number;
  fieldsCount: number;
  fieldType: FieldType;
}

@Component({
  components: {},
  filters: {
    formatDate,
    formatDateTime,
    translate
  }
})
export default class SafetyReportConfigurationTable extends Vue {

  private allInstrumentConfigs: Array<ISafetyReportConfigurationEntry> = [];
  private allVisitConfigs: Array<ISafetyReportConfigurationEntry> = [];
  private allPatientConfigs: Array<ISafetyReportConfigurationEntry> = [];

  tableHeaders: any = [
    {text: 'Typ', value: 'fieldDefinitionType', width: '120px'},
    {text: 'Gruppe', value: 'parentGroupingFieldIdentifier', width: '200px'},
    {text: 'Datenfeld', value: 'fieldDisplayKey'},
    {text: 'Export', value: 'export', width: '80px'},
  ];

  get loading() {
    return safetyModule.isLoading;
  }

  get selectedCount() {
    return this.allInstrumentConfigs.filter(field => field.isSelected).length +
        this.allVisitConfigs.filter(field => field.isSelected).length +
        this.allPatientConfigs.filter(field => field.isSelected).length;
  }

  get configuration() {
    return safetyModule.SafetyReportConfiguration;
  }

  get allHeaders(): Array<ExpansionHeader> {
    const instrumentHeaders = this.getSimpleHeaders(FieldType.Instrument);
    const baselineInstruments = instrumentHeaders
        .filter(i => i.parentEntityIdentifier.startsWith("baseline") || i.parentEntityIdentifier == "patientInclusion")
        .sort(this.expansionHeaderSortFn);
    const otherInstruments = instrumentHeaders
        .filter(i => !i.parentEntityIdentifier.startsWith("baseline") && i.parentEntityIdentifier != "patientInclusion")
        .sort(this.expansionHeaderSortFn);

    const visitHeaders = this.getSimpleHeaders(FieldType.Visit);
    visitHeaders.sort(this.expansionHeaderSortFn);

    const patientHeaders = this.getSimpleHeaders(FieldType.Patient);
    patientHeaders.sort(this.expansionHeaderSortFn);

    return [
      ...patientHeaders,
      ...visitHeaders,
      ...baselineInstruments,
      ...otherInstruments
   ];
  }

  getSimpleHeaders(fieldType: FieldType) {
    let configs: Array<ISafetyReportConfigurationEntry> = [];
    switch (fieldType) {
      case FieldType.Instrument: configs = this.allInstrumentConfigs; break;
      case FieldType.Visit: configs = this.allVisitConfigs; break;
      case FieldType.Patient: configs = this.allPatientConfigs; break;
      default: configs = [];
    }

    const identifierSet = new Set<string>();
    configs.forEach(conf => identifierSet.add(conf.referenceIdentifier!));

    const allHeaders: Array<ExpansionHeader> = [];

    // find texts and counters for each instrument header
    identifierSet.forEach(identifier => {
      const firstField = configs.find(field => field.referenceIdentifier == identifier);
      if (firstField == undefined) {
        console.error("firstField == undefined")
        return;
      }

      const fieldsCount = configs
          .filter(conf => conf.referenceIdentifier == firstField.referenceIdentifier)
          .length;
      const selectedCount = configs
          .filter(conf => conf.referenceIdentifier == firstField.referenceIdentifier)
          .filter(conf => conf.isSelected)
          .length;

      allHeaders.push(
          {
            parentEntityIdentifier: firstField.referenceIdentifier!,
            displayKey: firstField.referenceDisplayKey!,
            fieldsCount,
            selectedCount,
            fieldType: fieldType
          });
    });

    return allHeaders;
  }

  get fields() {
    return (expansionHeader: ExpansionHeader): ISafetyReportConfigurationEntry[] => {
      return [...this.allInstrumentConfigs, ...this.allVisitConfigs, ...this.allPatientConfigs]
          .filter(i => i.referenceIdentifier == expansionHeader.parentEntityIdentifier)
          .sort(this.fieldsSortFn);
    }
  }

  async mounted() {
    await this.initialize();
  }

  async initialize() {
    await safetyModule.loadSafetyReportConfiguration();

    this.allInstrumentConfigs.updateValues(deepCopy(safetyModule.SafetyReportConfiguration.instrumentFieldsConfiguration || []));
    this.allVisitConfigs.updateValues(deepCopy(safetyModule.SafetyReportConfiguration.visitFieldsConfiguration  || []));
    this.allPatientConfigs.updateValues(deepCopy(safetyModule.SafetyReportConfiguration.studyPatientFieldsConfiguration  || []));
  }

  async saveConfiguration() {
    const configuration = new SafetyReportConfigurationUpdateDto();

    configuration.fieldDefinitionIds = this.allInstrumentConfigs
        .filter(field => field.isSelected)
        .map(field => new SafetyReportConfigurationUpdateEntry({fieldDefinitionId: field.fieldDefinitionId}))
        .concat(this.allVisitConfigs
            .filter(field => field.isSelected)
            .map(field => new SafetyReportConfigurationUpdateEntry({fieldDefinitionId: field.fieldDefinitionId})))
        .concat(this.allPatientConfigs
            .filter(field => field.isSelected)
            .map(field => new SafetyReportConfigurationUpdateEntry({fieldDefinitionId: field.fieldDefinitionId})));

    await safetyModule.saveSafetyReportConfiguration({configuration});

    await this.initialize();
  }

  fieldsSortFn = (i1: ISafetyReportConfigurationEntry, i2: ISafetyReportConfigurationEntry) => {
    if (i1.parentGroupingFieldIdentifier! > i2.parentGroupingFieldIdentifier!) return 1;
    if (i1.parentGroupingFieldIdentifier! < i2.parentGroupingFieldIdentifier!) return -1;
    if (i1.order! > i2.order!) return 1;
    if (i1.order! < i2.order!) return -1;
    return 0;
  }

  expansionHeaderSortFn = (i1: ExpansionHeader, i2: ExpansionHeader) => {
    if (i1.parentEntityIdentifier == "patientInclusion") return -1;
    if (i2.parentEntityIdentifier == "patientInclusion") return 1;

    if (i1.parentEntityIdentifier! > i2.parentEntityIdentifier!) return 1;
    if (i1.parentEntityIdentifier! < i2.parentEntityIdentifier!) return -1;
    return 0;
  }
}
